/** Financials .xlsx export: one sheet per scenario, monthly base, drivers, sources & provenance. */
import { xlsxParts } from '../engine/underwritingXlsx.js';
import { downloadBytes } from '../engine/ooxmlZip.js';
import { DRIVERS, applyDrivers, quarterize } from './absFinancials.js';

const { buildWorkbook, S, XLSX_MIME } = xlsxParts;
const n = (v, d = 0) => (v == null || !Number.isFinite(v) ? 'missing' : +v.toFixed(d));

const LINES = [
  ['Kind', (q) => (q.mixed ? `${q.kind} (partial)` : q.kind)],
  ['Gross production (MMcf, pool, ARIES)', (q) => n(q.grossMcf != null ? q.grossMcf / 1e6 : null, 3)],
  ['Net production (MMcfe)', (q) => n(q.netMcfe != null ? q.netMcfe / 1e6 : null, 3)],
  ['Realized price ($/Mcfe)', (q) => n(q.realizedPrice, 3)],
  ['Revenue ($k)', (q) => n(q.revenue != null ? q.revenue / 1e3 : null)],
  ['Hedge settlements ($k)', (q) => n(q.hedge != null ? q.hedge / 1e3 : null)],
  ['LOE ($k)', (q) => n(q.loe != null ? q.loe / 1e3 : null)],
  ['Production taxes ($k)', (q) => n(q.taxes != null ? q.taxes / 1e3 : null)],
  ['Capex ($k)', (q) => n(q.capex != null ? q.capex / 1e3 : null)],
  ['NCF ($k)', (q) => n(q.ncf != null ? q.ncf / 1e3 : null)],
  ['ABS debt service, UW schedule ($k)', (q) => (q.kind === 'actual' ? '' : n(q.absDebtService != null ? q.absDebtService / 1e3 : null))],
  ['DSCR (x)', (q) => (q.kind === 'actual' ? '' : n(q.dscr, 2))],
  ['Cash to parent, base ($k)', (q) => (q.kind === 'actual' ? '' : n(q.absToParent != null ? q.absToParent / 1e3 : null))],
  ['Notes outstanding, base ($k)', (q) => (q.kind === 'actual' ? '' : n(q.absNotes != null ? q.absNotes / 1e3 : null))],
  ['Edited cells', (q) => q.edited.join(', ')],
];

function scenarioSheet(name, quarters) {
  const header = { values: ['Line', ...quarters.map((q) => q.quarter)], styles: [S.header, ...quarters.map(() => S.header)] };
  return { name, rows: [header, ...LINES.map(([l, f]) => [l, ...quarters.map(f)])] };
}

export function financialsSheets(data, { drivers, presets, overrides, scenario }) {
  const scen = data.scenarios || { base: 1 };
  const sheets = [
    {
      name: 'Cover',
      rows: [
        { values: [`D2D Energy · ${data.dealId} · Financials`], styles: [S.brandTitle] },
        { values: ['History → forecast (approved DCA) → ABS cash flows'], styles: [S.brandSub] },
        [],
        ['Active scenario', scenario],
        ['Forecast start', data.forecastStart],
        ['Actual months / with LOS $', `${data.actualMonths} / ${data.losMonths}`],
        ['Approved case', data.approvedCaseId],
        ['Missing values', 'Cells marked "missing" are not in any source; nothing is estimated for history.'],
      ],
    },
  ];
  for (const s of ['base', 'upside', 'downside']) {
    const dv = { ...drivers, volumeMult: s === scenario ? drivers.volumeMult : (scen[s] ?? 1) };
    sheets.push(scenarioSheet(s[0].toUpperCase() + s.slice(1), quarterize(applyDrivers(data.months, dv, s === scenario ? overrides : {}))));
  }
  const monthly = applyDrivers(data.months, drivers, overrides);
  sheets.push({
    name: 'Monthly (active)',
    rows: [
      { values: ['Month', 'Kind', 'Gross Mcf', 'Net Mcfe', 'Price', 'Revenue', 'Hedge', 'LOE', 'Taxes', 'Capex', 'NCF', 'ABS DS', 'To parent', 'Notes', 'Missing', 'Source'], styles: Array(16).fill(S.header) },
      ...monthly.map((m) => [m.month, m.kind, n(m.grossMcf), n(m.netMcfe), n(m.realizedPrice, 3), n(m.revenue), n(m.hedge), n(m.loe), n(m.taxes), n(m.capex), n(m.ncf),
        m.kind === 'forecast' ? n(m.absDebtService) : '', m.kind === 'forecast' ? n(m.absToParent) : '', m.kind === 'forecast' ? n(m.absNotes) : '', (m.missing || []).join(', '), m.source || '']),
    ],
  });
  sheets.push({
    name: 'Drivers',
    rows: [
      { values: ['Driver', 'Preset', 'Edited'], styles: [S.header, S.header, S.header] },
      ...DRIVERS.map((d) => [d.label, presets[d.key], drivers[d.key]]),
      [],
      { values: ['Cell overrides (line|quarter)', 'Value'], styles: [S.header, S.header] },
      ...Object.entries(overrides).map(([k, v]) => [k, v]),
    ],
  });
  const p = data.provenance || {};
  sheets.push({
    name: 'Sources & provenance',
    rows: [
      { values: ['Item', 'Value'], styles: [S.header, S.header] },
      ['Package source', data.packageSource],
      ['Historical $ source', data.losSource || 'missing — no LOS in package'],
      ['Historical production source', 'asset-workflow history (ARIES AC_PRODUCT), pool-level'],
      ['Forecast source', 'approved DCA fits → deal-package.v1 → ABS engine packageMonth'],
      ['Review source', data.reviewSource],
      ['Freeze', data.freeze ? `#${data.freeze.freezeId} ${data.freeze.contentSha256}` : 'none (live approvals)'],
      ['Approved case id', data.approvedCaseId],
      ['Pool digest', p.poolDigest],
      ['Review digest', p.reviewDigest],
      ['Basis counts', JSON.stringify(p.basisCounts || {})],
      ['runHash base / upside / downside', `${data.runHashes?.base} / ${data.runHashes?.upside} / ${data.runHashes?.downside}`],
      ['Scenario volume ×', JSON.stringify(scen)],
      ...(data.notes || []).map((t, i) => [`Note ${i + 1}`, t]),
    ],
  });
  return sheets;
}

export function downloadFinancialsXlsx(data, opts) {
  downloadBytes(`${data.dealId}-financials.xlsx`, buildWorkbook(financialsSheets(data, opts)), XLSX_MIME);
}
